import { Tooltip } from '@brightdrop/bd-ui';
import { IconButton } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import { withTraceId } from '../../lifecycle/tracker.helpers';
import type { ArtifactRef } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { ShareGlyph } from '../icons';
import useMessageActionButtonStyles from '../messageActionButton.styles';
import ShareViaEmailDialog, {
  type ShareRecipient,
} from '../ShareViaEmailDialog';
import { showSidekickErrorToast, showSidekickSuccessToast } from '../toast';
import { SHARE_TYPE, type ShareType } from './share-target';
import { useShareResponse } from './useShareResponse';
import { isShareMaybeSentError, isShareRateLimitError } from './utils';

const SHARE_ICON_SIZE_PX = 16;

/**
 * The toast copy for a failed share. A send whose outcome is unknown
 * must not invite a retry, which could mail the recipients twice.
 */
const getShareErrorToastKey = (error: unknown): string => {
  if (isShareRateLimitError(error)) {
    return 'chat.share.dialog.rateLimitToast';
  }
  if (isShareMaybeSentError(error)) {
    return 'chat.share.dialog.maybeSentToast';
  }
  return 'chat.share.dialog.errorToast';
};

/** Props for the per-message share action. */
export type MessageShareButtonProps = {
  /** Conversation the shared response belongs to. */
  conversationId: string;
  /** Id of the assistant response being shared. */
  messageId: string;
  /** Raw text of the response, used to find its stored copy. */
  content: string;
  /** When the response arrived (ISO 8601), used to tell identical answers apart. */
  createdAt: string;
  /** What sharing sends: the response alone, or its table or chart. Defaults to the response. */
  shareType?: ShareType;
  /** The tables and charts the response showed, when it carried them. */
  artifacts?: ReadonlyArray<ArtifactRef>;
  /** Backend trace of the response, reported to the host with a share. */
  traceId?: string;
  /** `data-testid` for the button element. */
  testId: string;
  className?: string;
};

/**
 * Opens the share-via-email modal for one assistant response and sends
 * it once the user presses Send.
 *
 * The button owns whether the modal is open and reports how the send
 * went; the modal owns the recipients inside it. A rate-limited share
 * says to wait rather than retry, since another attempt inside the
 * hour is refused the same way.
 */
function MessageShareButton({
  conversationId,
  messageId,
  content,
  createdAt,
  shareType,
  artifacts,
  traceId,
  testId,
  className,
}: MessageShareButtonProps) {
  const { classes: buttonClasses, cx } = useMessageActionButtonStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { captureEmit } = useSidekickFeatureEventReporter();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const shareResponse = useShareResponse({
    conversationId,
    messageId,
    content,
    createdAt,
    shareType,
    artifacts,
  });

  const handleOpen = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleSubmit = useCallback(
    async (recipients: ShareRecipient[]) => {
      const emitShared = captureEmit();
      try {
        await shareResponse(recipients);
        showSidekickSuccessToast({
          message: t('chat.share.dialog.sentToast'),
          closeAriaLabel: t('chat.share.dialog.sentToastDismissAriaLabel'),
          testId: 'ai-sidekick-share-sent-toast',
        });
        emitShared({
          type: SIDEKICK_FEATURE_EVENT.RESPONSE_SHARED,
          conversationId,
          ...withTraceId(traceId),
          shareType: shareType ?? SHARE_TYPE.RESPONSE,
          recipientCount: recipients.length,
        });
      } catch (error) {
        showSidekickErrorToast({
          message: t(getShareErrorToastKey(error)),
          closeAriaLabel: t('chat.share.dialog.errorToastDismissAriaLabel'),
          testId: 'ai-sidekick-share-error-toast',
        });
      }
    },
    [captureEmit, conversationId, shareResponse, shareType, t, traceId]
  );

  return (
    <>
      <Tooltip
        placement="top"
        content={t('chat.share.tooltip')}
        trigger={
          <IconButton
            className={cx(
              buttonClasses.button,
              isDialogOpen && buttonClasses.buttonActive,
              className
            )}
            onClick={handleOpen}
            aria-label={t('chat.share.ariaLabel')}
            data-testid={testId}
          >
            <ShareGlyph size={SHARE_ICON_SIZE_PX} />
          </IconButton>
        }
      />
      <ShareViaEmailDialog
        open={isDialogOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default MessageShareButton;
